import type { ComponentProps } from 'react';
import type { Can } from '@/lib/permissions';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

type Props = ComponentProps<typeof Button> & {
  /** From useCan: hidden → nothing renders; not allowed → disabled with its reason. */
  can?: Can;
  /** Another reason to hold the action back (a capability the API reports as unavailable, a missing prerequisite). */
  blockedReason?: string;
  /** Tooltip while the action is allowed: what it does (a blocked button shows its reason instead). */
  hint?: string;
};

/**
 * A button that explains why it cannot be used: `aria-disabled` (still focusable, so the tooltip and the screen reader
 * get the reason) instead of a silent `disabled`. Guests and clients never see actions they can never take.
 */
export function GatedButton({ can, blockedReason, hint, onClick, children, ...props }: Props) {
  if (can?.hidden) return null;
  const reason = can && !can.allowed ? can.reason ?? 'Not allowed for your role' : blockedReason;
  if (!reason) {
    const button = (
      <Button onClick={onClick} {...props}>
        {children}
      </Button>
    );
    if (!hint) return button;
    return (
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent>{hint}</TooltipContent>
      </Tooltip>
    );
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button {...props} aria-disabled data-blocked-reason={reason} onClick={(e) => e.preventDefault()}>
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{reason}</TooltipContent>
    </Tooltip>
  );
}
